/**
 * MachineRegistry — `Map<ChatId, Actor>` of per-chat `chatStreamMachine`
 * actors.
 *
 * Sibling to [ChatStreamRegistry](../stream/ChatStreamRegistry.ts) — the
 * stream registry holds DATA (chunks), this one holds LIFECYCLE (FSM
 * actors). They're kept separate because.
 */

import type { ChatStreamRegistry } from 'src/content/ChatV2/stream/ChatStreamRegistry';
import type { ChatId } from 'src/content/ChatV2/types/ids';
import logger from 'src/utils/logger';
import { type Actor, createActor } from 'xstate';

import { isStreamingState } from './actions';
import { chatStreamMachine } from './chatStreamMachine';
import { createMachineInspector } from './inspector';

export type ChatStreamActor = Actor<typeof chatStreamMachine>;

export class MachineRegistry {
  private readonly actors = new Map<ChatId, ChatStreamActor>();

  private readonly streamingChats = new Set<ChatId>();
  private readonly streamingListeners = new Set<() => void>();
  private streamingSnapshot: ReadonlySet<ChatId> = new Set();

  constructor(private readonly streamRegistry: ChatStreamRegistry) {}

  subscribeToStreaming(listener: () => void): () => void {
    this.streamingListeners.add(listener);
    return () => {
      this.streamingListeners.delete(listener);
    };
  }

  getStreamingChats(): ReadonlySet<ChatId> {
    return this.streamingSnapshot;
  }

  private setStreaming(chatId: ChatId, streaming: boolean): void {
    const changed = streaming
      ? !this.streamingChats.has(chatId) && !!this.streamingChats.add(chatId)
      : this.streamingChats.delete(chatId);
    if (!changed) {
      return;
    }
    this.streamingSnapshot = new Set(this.streamingChats);
    this.streamingListeners.forEach((listener) => {
      try {
        listener();
      } catch (error) {
        /* One bad subscriber must not stop the others. Two notifications per
         * turn, so logging costs nothing. */
        logger.error('[chat] streaming listener threw', { chatId, error });
      }
    });
  }

  get(chatId: ChatId): ChatStreamActor {
    const existing = this.actors.get(chatId);
    if (existing) {
      return existing;
    }

    const entry = this.streamRegistry.get(chatId);
    const actor = createActor(chatStreamMachine, {
      input: { chatId, buffer: entry.buffer },
      inspect: createMachineInspector(chatId)
    });
    actor.start();
    entry.machineActor = actor;

    actor.subscribe((snapshot) =>
      this.setStreaming(chatId, isStreamingState(String(snapshot.value)))
    );

    this.actors.set(chatId, actor);
    return actor;
  }

  peek(chatId: ChatId): ChatStreamActor | undefined {
    return this.actors.get(chatId);
  }

  has(chatId: ChatId): boolean {
    return this.actors.has(chatId);
  }

  dispose(chatId: ChatId): void {
    const actor = this.actors.get(chatId);
    if (!actor) {
      return;
    }
    actor.stop();
    this.actors.delete(chatId);
    this.setStreaming(chatId, false);
    this.streamRegistry.dispose(chatId);
  }

  disposeAll(): void {
    for (const actor of this.actors.values()) {
      actor.stop();
    }
    this.actors.clear();
    for (const chatId of [...this.streamingChats]) {
      this.setStreaming(chatId, false);
    }
    this.streamRegistry.disposeAll();
  }

  chatIds(): ChatId[] {
    return [...this.actors.keys()];
  }

  get size(): number {
    return this.actors.size;
  }
}
